import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { invitationService } from '../services/invitationService';
import { SendInvitationsData } from '../types';
import toast from 'react-hot-toast';

export const useSendInvitations = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: SendInvitationsData) => invitationService.sendInvitations(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
      toast.success('Invitations sent successfully!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to send invitations');
    },
  });
};

export const useRaffleInvitations = (raffleId: string) => {
  return useQuery({
    queryKey: ['invitations', 'raffle', raffleId],
    queryFn: () => invitationService.getRaffleInvitations(raffleId),
    enabled: !!raffleId,
  });
};

export const useSentInvitations = (params?: { raffleId?: string }) => {
  return useQuery({
    queryKey: ['invitations', 'sent', params?.raffleId],
    queryFn: () => invitationService.getRaffleInvitations(params?.raffleId!),
    enabled: !!params?.raffleId,
  });
};

export const useResendInvitation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => invitationService.resendInvitation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
      toast.success('Invitation resent successfully!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to resend invitation');
    },
  });
};

export const useDeleteInvitation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => invitationService.deleteInvitation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
      toast.success('Invitation deleted successfully!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete invitation');
    },
  });
};

export const useReceivedInvitations = () => {
  return useQuery({
    queryKey: ['invitations', 'received'],
    queryFn: () => invitationService.getReceivedInvitations(),
  });
};

export const useAcceptInvitation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => invitationService.acceptInvitation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations', 'received'] });
      toast.success('Invitation accepted!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to accept invitation');
    },
  });
};

export const useDeclineInvitation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => invitationService.declineInvitation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations', 'received'] });
      toast.success('Invitation declined!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to decline invitation');
    },
  });
};

export const useAddCodeRaffle = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: { raffleId: string; accessCode: string }) =>
      invitationService.addCodeRaffle(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations', 'code-raffles'] });
      toast.success('Raffle added successfully!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to add raffle');
    },
  });
};

export const useRemoveCodeRaffle = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (raffleId: string) => invitationService.removeCodeRaffle(raffleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations', 'code-raffles'] });
      toast.success('Raffle removed successfully!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to remove raffle');
    },
  });
};

export const useCodeRaffles = () => {
  return useQuery({
    queryKey: ['invitations', 'code-raffles'],
    queryFn: () => invitationService.getCodeRaffles(),
  });
};
